import type { CatalogListConfig } from './types';
import { initTooltips, resolveUrl, results } from './catalog-list';
import { bookChapters, type BookChapter } from './world-book-chapters';

interface NavigationNode {
    id: string; name: string; type_icon: string; url: string; children: NavigationNode[];
}
interface ShellBook {
    ok: boolean; error?: string;
    world: { id: string; name: string; type_icon: string };
    chapters: NavigationNode[];
    atlas: NavigationNode[];
    history: { tracks: { id: string }[]; eras: { id: string }[] } | [];
}
interface PageTarget {
    target: string; name: string; url: string; chapter?: BookChapter;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    node.className = cls;
    node.textContent = text;
    return node;
}
function link(text: string, url: string, cls = ''): HTMLAnchorElement {
    const node = el('a', cls, text);
    node.href = resolveUrl(url);
    return node;
}
let mountSequence = 0;

export function mountCatalogWorldBookRail(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    const shell = root.closest<HTMLElement>('.ft-wb-shell--page');
    if (!container || !shell) return;
    const pager = shell.querySelector<HTMLElement>('[data-wb-pager]');
    const worldId = root.getAttribute('data-ft-location-id') || '';
    const activeKind = root.getAttribute('data-ft-active-kind') || 'article';
    const activeId = (root.getAttribute('data-ft-active-id') || '').toUpperCase();
    const mountId = `wb-article-${++mountSequence}`;
    const load = async (): Promise<void> => {
        try {
            const url = new URL(resolveUrl(config.endpoint), window.location.href);
            url.searchParams.set('location_id', worldId);
            url.searchParams.set('shell', '1');
            const response = await fetch(url, { credentials: 'same-origin' });
            const book = await response.json() as ShellBook;
            if (!response.ok || book.ok !== true) throw new Error(book.error || `HTTP ${response.status}`);
            const chapters = bookChapters(book);
            const coverUrl = `${resolveUrl('location.php')}?${new URLSearchParams({ location_id: book.world.id })}`;
            const chapterUrl = (key: string): string => `${coverUrl}#wb-ch-${key}`;
            const articles = new Map(book.chapters.map(article => [article.id, article]));
            const pages: PageTarget[] = [];
            const opened = new Set<string>();
            // The tree's stored order is authoritative: a category is a page before its children.
            const walk = (article: NavigationNode, chapter: BookChapter, path: string[]): void => {
                const here = [...path, article.id];
                pages.push({ target: article.id, name: article.name, url: article.url, chapter });
                if (activeKind === 'article' && article.id.toUpperCase() === activeId) here.forEach(id => opened.add(id));
                article.children.forEach(child => walk(child, chapter, here));
            };
            chapters.forEach(chapter => {
                const article = articles.get(chapter.key);
                if (article) walk(article, chapter, []);
            });
            const currentIndex = activeKind === 'article' ? pages.findIndex(page => page.target.toUpperCase() === activeId) : -1;
            const openPlaces = (places: NavigationNode[], path: string[]): void => {
                for (const place of places) {
                    const here = [...path, place.id];
                    if (activeKind === 'place' && place.id.toUpperCase() === activeId) here.forEach(id => opened.add(id));
                    openPlaces(place.children, here);
                }
            };
            openPlaces(book.atlas, ['atlas']);
            const controls = new Map<string, { button: HTMLButtonElement; children: HTMLElement }[]>();
            const setBranch = (id: string, open: boolean): void => {
                if (open) opened.add(id); else opened.delete(id);
                for (const control of controls.get(id) || []) {
                    control.button.setAttribute('aria-expanded', String(open));
                    control.button.textContent = open ? '▾' : '▸';
                    control.children.hidden = !open;
                }
            };
            const contents = (view: string): HTMLElement => {
                const nav = el('nav', 'ft-wb-toc ft-wb-article-toc');
                nav.setAttribute('aria-label', 'World Book contents');
                const head = link('', coverUrl, 'ft-wb-rail-head');
                const glyph = el('i', 'ft-icon');
                glyph.dataset.icon = book.world.type_icon || 'world';
                glyph.dataset.w = '26'; glyph.dataset.h = '26';
                glyph.setAttribute('aria-hidden', 'true');
                const title = el('span');
                title.append(el('span', 'ft-wb-kicker', 'World Book'), el('span', 'ft-wb-rail-name', book.world.name));
                head.append(glyph, title);
                nav.append(head, el('div', 'ft-wb-kicker', 'Contents'));
                const node = (article: NavigationNode, depth: number, chapter?: BookChapter, kind = 'article'): HTMLElement => {
                    const item = el('li');
                    const row = el('div', 'ft-wb-article-row');
                    if (article.id !== 'atlas') row.setAttribute(kind === 'place' ? 'data-wb-place-id' : 'data-wb-article-id', article.id);
                    row.style.paddingLeft = `${0.35 + depth * 0.65}rem`;
                    const a = link(chapter ? `${chapter.numeral}. ${chapter.title}` : article.name, article.url);
                    a.title = a.textContent || '';
                    if (chapter) a.setAttribute('data-wb-toc', chapter.key);
                    if (kind === activeKind && article.id.toUpperCase() === activeId) {
                        row.classList.add('is-active'); a.setAttribute('aria-current', 'page');
                    }
                    if (article.children.length) {
                        const button = el('button', 'ft-wb-branch'); button.type = 'button';
                        button.setAttribute('data-wb-branch', article.id);
                        button.setAttribute('aria-label', `Toggle ${article.name}`);
                        const children = el('ul', 'ft-wb-article-children');
                        children.id = `${mountId}-${view}-${article.id}`;
                        children.hidden = !opened.has(article.id);
                        button.setAttribute('aria-controls', children.id);
                        button.setAttribute('aria-expanded', String(!children.hidden));
                        button.textContent = children.hidden ? '▸' : '▾';
                        const pair = { button, children };
                        controls.set(article.id, [...(controls.get(article.id) || []), pair]);
                        button.addEventListener('click', () => setBranch(article.id, !opened.has(article.id)));
                        article.children.forEach(child => children.append(node(child, depth + 1, undefined, kind)));
                        row.append(button, a); item.append(row, children);
                    } else {
                        row.append(el('span', 'ft-wb-branch-space'), a); item.append(row);
                    }
                    return item;
                };
                const list = el('ul', 'ft-wb-article-chapters');
                chapters.forEach(chapter => {
                    const article = articles.get(chapter.key);
                    if (chapter.key === 'atlas') {
                        list.append(node({ id: 'atlas', name: chapter.title, type_icon: 'location', url: chapterUrl('atlas'),
                            children: book.atlas }, 0, chapter, 'place'));
                    } else if (article) list.append(node(article, 0, chapter));
                    else {
                        const item = el('li', 'ft-wb-section-row');
                        const a = link(`${chapter.numeral}. ${chapter.title}`, chapterUrl(chapter.key));
                        a.setAttribute('data-wb-toc', chapter.key); item.append(a); list.append(item);
                    }
                });
                nav.append(list);
                return nav;
            };
            const rail = el('div', 'ft-wb-article-rail'); rail.setAttribute('data-wb-contents', '');
            rail.append(contents('desktop'));
            const mobile = el('div', 'ft-wb-mobile-contents');
            const open = el('button', 'btn ft-wb-contents-pill', '☰ Contents'); open.type = 'button';
            open.setAttribute('data-wb-contents-open', '');
            open.setAttribute('data-bs-toggle', 'offcanvas'); open.setAttribute('data-bs-target', `#${mountId}-contents`);
            open.setAttribute('aria-controls', `${mountId}-contents`); mobile.append(open);
            const offcanvas = el('div', 'offcanvas offcanvas-start ft-wb-offcanvas');
            offcanvas.id = `${mountId}-contents`; offcanvas.tabIndex = -1;
            offcanvas.setAttribute('data-wb-contents-mobile', '');
            offcanvas.setAttribute('aria-label', `${book.world.name} contents`);
            const close = el('button', 'btn-close btn-close-white ms-auto m-3'); close.type = 'button';
            close.setAttribute('data-bs-dismiss', 'offcanvas'); close.setAttribute('aria-label', 'Close contents');
            const mobileNav = contents('mobile');
            mobileNav.querySelectorAll('a').forEach(a => a.addEventListener('click', () => close.click()));
            offcanvas.append(close, mobileNav);
            // Construct both views and the pager before publishing anything. A failed load is silent.
            const cards = document.createDocumentFragment();
            if (currentIndex >= 0) {
                const currentChapter = pages[currentIndex]!.chapter;
                const endKey = (Array.isArray(book.history) ? book.history.length : book.history.tracks.length || book.history.eras.length) ? 'history' : 'index';
                const previous = pages[currentIndex - 1] || { target: 'world', name: `Foreword · ${book.world.name}`, url: coverUrl };
                const next = pages[currentIndex + 1] || { target: `chapter:${endKey}`, name: endKey === 'history' ? 'History' : 'Index',
                    url: chapterUrl(endKey), chapter: chapters.find(chapter => chapter.key === endKey) };
                for (const [direction, target] of [['prev', previous], ['next', next]] as const) {
                    const a = link('', target.url, `ft-wb-page-card ft-wb-page-card--${direction}`);
                    a.setAttribute(`data-wb-${direction}`, ''); a.setAttribute('data-wb-target', target.target);
                    a.rel = direction;
                    a.append(el('span', 'ft-wb-kicker', direction === 'prev' ? 'Previous' : 'Next'),
                        el('span', 'ft-wb-page-name', target.name));
                    if (target.chapter && target.chapter.key !== currentChapter?.key) {
                        a.append(el('span', 'ft-wb-page-chapter', `Chapter ${target.chapter.numeral} · ${target.chapter.title}`));
                    }
                    cards.append(a);
                }
            }
            container.replaceChildren(rail, mobile, offcanvas);
            pager?.replaceChildren(cards);
            const revealActive = (scroller: HTMLElement): void => {
                const row = scroller.querySelector<HTMLElement>('.is-active');
                if (row) scroller.scrollTop += row.getBoundingClientRect().top - scroller.getBoundingClientRect().top - scroller.clientHeight / 2;
            };
            revealActive(rail);
            offcanvas.addEventListener('shown.bs.offcanvas', () => revealActive(offcanvas));
            initTooltips(container);
        } catch {
            container.replaceChildren(); pager?.replaceChildren();
            root.closest('aside')?.setAttribute('hidden', '');
            shell.classList.add('ft-wb-shell--unavailable');
        }
    };
    void load();
}
